// Keep explicit connection prompts independent of the dynamically loaded wallet SDK.
import { getMetaMaskConnectionMode, LOCAL_FLASK_SNAP_ID } from './metamask-connection-mode';
import { getMetaMaskProvider, type MetaMaskProvider } from './metamask-provider';
import { WalletAccessDeniedError } from './wallet-connection-error';
import { hasPendingWalletConnection, requestWalletAccountPermissions, requestWalletConnection } from './wallet-connection-request';

const DEFAULT_SNAP_ID = 'npm:@ducat-unit/wallet-snap';
const DEFAULT_SNAP_VERSION = '^0.3.1';

function isAccessDenied(error: unknown, depth = 0): boolean {
  if (!error || typeof error !== 'object' || depth > 3) return false;
  const value = error as { message?: unknown; cause?: unknown; data?: { originalError?: unknown } };
  return (
    (typeof value.message === 'string' && /(?:Non-200 status code:\s*['"]?403\b|\bHTTP\s+403\b)/i.test(value.message)) ||
    isAccessDenied(value.cause, depth + 1) ||
    isAccessDenied(value.data?.originalError, depth + 1)
  );
}

export function getSnapId(): string {
  if (getMetaMaskConnectionMode() === 'flask') return LOCAL_FLASK_SNAP_ID;
  return process.env.NEXT_PUBLIC_DUCAT_SNAP_ID || DEFAULT_SNAP_ID;
}

function getSnapVersion(): string | undefined {
  return process.env.NEXT_PUBLIC_DUCAT_SNAP_VERSION || DEFAULT_SNAP_VERSION;
}

export async function requestSnapConnection(provider?: MetaMaskProvider): Promise<MetaMaskProvider> {
  const metamaskProvider = provider ?? (await getMetaMaskProvider());
  const snapId = getSnapId();
  const snapVersion = getSnapVersion();
  const requestParams = snapVersion && snapId.startsWith('npm:') ? { version: snapVersion } : {};

  const snapRequest = { method: 'wallet_requestSnaps', params: { [snapId]: requestParams } };
  // Request interactive account access before Snap work, even when access is cached.
  // A timed-out Snap prompt remains alive: retry it without opening a second picker.
  let stage: 'account-access' | 'snap-connection' = 'account-access';
  try {
    if (!hasPendingWalletConnection(metamaskProvider, snapRequest)) {
      await requestWalletAccountPermissions(metamaskProvider);
    }
    stage = 'snap-connection';
    await requestWalletConnection(metamaskProvider, snapRequest);
  } catch (error) {
    if (isAccessDenied(error)) throw new WalletAccessDeniedError(stage);
    const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown };
    const unsupportedMessage =
      typeof message === 'string' &&
      /wallet_requestSnaps/i.test(message) &&
      /not found|not supported|unsupported|corresponding handler/i.test(message);
    if (code === -32601 || code === 4200 || unsupportedMessage) {
      throw new Error('Ducat requires MetaMask with Snaps support. Use the latest MetaMask desktop browser extension.');
    }
    throw error;
  }

  return metamaskProvider;
}
